import React, { useState, useEffect, useRef } from 'react';
import { Controller } from 'react-hook-form';
import api from '../api/requests';

const BusinessDropdown = ({ control, name, required, onChange, label = 'Related Businesses', initialValue = [] }) => {
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedBusinesses, setSelectedBusinesses] = useState([]);
  const initializedRef = useRef(false);

  // Load initial businesses
  useEffect(() => {
    const loadInitialBusinesses = async () => {
      setLoading(true);
      try {
        const response = await api.businesses.getAll({ page: 1, limit: 20 });
        const data = response.data?.data || response.data;
        const businessList = data?.businesses || data || [];
        const formattedBusinesses = businessList.map(business => ({
          value: business._id,
          label: business.title,
          description: business.description || ''
        }));
        
        setBusinesses(formattedBusinesses);
        setPages(Math.ceil((data?.total || businessList.length) / 20));
      } catch (error) {
        console.error('Error loading businesses:', error);
        setBusinesses([]);
      } finally {
        setLoading(false);
      }
    };

    loadInitialBusinesses();
  }, []);

  // Initialize selected businesses when businesses are loaded
  useEffect(() => {
    if (businesses.length > 0 && !initializedRef.current) {
      initializedRef.current = true;
    }
  }, [businesses]);

  // Load more businesses
  useEffect(() => {
    const loadMoreBusinesses = async () => {
      if (page === 1) return;
      
      setLoading(true);
      try {
        const params = { page, limit: 20 };
        if (searchTerm) params.search = searchTerm;
        
        const response = await api.businesses.getAll(params);
        const data = response.data?.data || response.data;
        const businessList = data?.businesses || data || [];
        const formattedBusinesses = businessList.map(business => ({
          value: business._id,
          label: business.title,
          description: business.description || ''
        }));
        
        setBusinesses(prev => [...prev, ...formattedBusinesses]);
      } catch (error) {
        console.error('Error loading more businesses:', error);
      } finally {
        setLoading(false);
      }
    };

    loadMoreBusinesses();
  }, [page, searchTerm]);

  const handleLoadMore = () => {
    setPage(prev => prev + 1);
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setPage(1);
    setBusinesses([]);
  };

  const handleSelectBusiness = (business, fieldOnChange) => {
    const isSelected = selectedBusinesses.some(selected => selected.value === business.value);
    let newSelected;
    
    if (isSelected) {
      newSelected = selectedBusinesses.filter(selected => selected.value !== business.value);
    } else {
      newSelected = [...selectedBusinesses, business];
    }
    
    setSelectedBusinesses(newSelected);
    // Send only the IDs to the backend
    const businessIds = newSelected.map(b => b.value);
    fieldOnChange(businessIds);
  };

  const handleSelectAll = (fieldOnChange) => {
    // Get all currently visible businesses that are not already selected
    const unselectedFilteredBusinesses = filteredBusinesses.filter(business => 
      !selectedBusinesses.some(selected => selected.value === business.value)
    );
    const newSelected = [...selectedBusinesses, ...unselectedFilteredBusinesses];
    setSelectedBusinesses(newSelected);
    // Send only the IDs to the backend
    const businessIds = newSelected.map(b => b.value);
    fieldOnChange(businessIds);
  };

  const handleDeselectAll = (fieldOnChange) => {
    // Only deselect businesses that are currently visible in the filtered results
    const filteredBusinessIds = filteredBusinesses.map(b => b.value);
    const remainingSelected = selectedBusinesses.filter(selected => 
      !filteredBusinessIds.includes(selected.value)
    );
    setSelectedBusinesses(remainingSelected);
    // Send only the IDs to the backend
    const businessIds = remainingSelected.map(b => b.value);
    fieldOnChange(businessIds);
  };

  // Filter businesses based on search term
  const filteredBusinesses = businesses.filter(business =>
    business.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    business.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Initialize selected businesses from initial value
  useEffect(() => {
    if (initialValue && Array.isArray(initialValue) && initialValue.length > 0 && businesses.length > 0 && selectedBusinesses.length === 0) {
      if (typeof initialValue[0] === 'string') {
        // If initialValue contains IDs, find the corresponding business objects
        const businessObjects = initialValue.map(id => 
          businesses.find(b => b.value === id) || { value: id, label: `Business ${id}`, description: '' }
        );
        setSelectedBusinesses(businessObjects);
      } else if (typeof initialValue[0] === 'object') {
        // If initialValue contains full objects, use them directly
        setSelectedBusinesses(initialValue);
      }
    }
  }, [initialValue, businesses, selectedBusinesses.length]);


  return (
    <Controller
      control={control}
      name={name}
      rules={{ required: required ? `${label} is required` : false }}
      render={({ field }) => {
        return (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            
            {/* Selected Businesses Display */}
            <div className="flex flex-wrap gap-2 min-h-[40px] p-2 border border-gray-200 rounded-md bg-gray-50">
              {selectedBusinesses.map((business, index) => (
                <div
                  key={business.value}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                >
                  <span>{business.label}</span>
                  <button
                    type="button"
                    onClick={() => handleSelectBusiness(business, field.onChange)}
                    className="text-blue-600 hover:text-blue-800 font-bold"
                    title="Remove business"
                  >
                    ×
                  </button>
                </div>
              ))}
              {selectedBusinesses.length === 0 && (
                <span className="text-gray-400 text-sm">No businesses selected</span>
              )}
            </div>

            {/* Search and Select Interface */}
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                onFocus={() => setIsOpen(true)}
                placeholder="Search businesses to select..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              {/* Dropdown */}
              {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
                  {/* Select All/Deselect All */}
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectAll(field.onChange);
                        }}
                        className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeselectAll(field.onChange);
                        }}
                        className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Deselect All
                      </button>
                      <span className="text-xs text-gray-500 self-center ml-2">
                        {selectedBusinesses.length} selected
                      </span>
                    </div>
                  </div>

                  {/* Scrollable Business List */}
                  <div className="max-h-64 overflow-y-auto">
                    {loading ? (
                      <div className="p-4 text-center text-gray-500">Loading businesses...</div>
                    ) : filteredBusinesses.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No businesses found</div>
                    ) : (
                      filteredBusinesses.map((business) => {
                        const isSelected = selectedBusinesses.some(selected => selected.value === business.value);
                        return (
                          <div
                            key={business.value}
                            className={`p-3 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0 ${
                              isSelected ? 'bg-blue-50' : ''
                            }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectBusiness(business, field.onChange);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="font-medium text-sm">{business.label}</div>
                              </div>
                              <div className="ml-2">
                                {isSelected ? (
                                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs">✓</span>
                                  </div>
                                ) : (
                                  <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Load More */}
                  {page < pages && (
                    <div className="p-2 border-t border-gray-200 bg-white">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLoadMore();
                        }}
                        className="w-full px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded"
                      >
                        Load More ({page + 1} of {pages})
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Click outside to close */}
            {isOpen && (
              <div
                className="fixed inset-0 z-30"
                onClick={() => setIsOpen(false)}
              />
            )}
          </div>
        );
      }}
    />
  );
};

export default BusinessDropdown;